import React, { useEffect, useState } from 'react';
import { Camera, Cross, CalendarDays, PauseCircle, Loader2 } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { api } from '@/lib/api';
import { toast } from 'sonner';

const META = {
  photos: { icon: Camera, title: 'Fotos de Google', help: 'Renovación semanal de fotos caducadas. Apagado: las fotos no se tocan (las caducadas seguirán rotas hasta que Grokbot suba las suyas).' },
  pharmacies: { icon: Cross, title: 'Farmacias de guardia', help: 'Importación mensual del PDF oficial del COF. Apagado: solo entran farmacias por la API de Grokbot.' },
  events: { icon: CalendarDays, title: 'Eventos', help: 'Importación diaria de Ticketmaster, WebTenerife, Santa Cruz, ICDCultural y Lucha Canaria. Apagado: solo entran eventos por la API de Grokbot.' },
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }) : '');

export const AutomationPanel = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => { api.getAutomation().then(r => setStatus(r.data)).catch(() => toast.error('No se pudo cargar el estado de importaciones')); }, []);

  const toggle = async (kind, enabled) => {
    setBusy(kind);
    try {
      const r = await api.setAutomation(kind, enabled);
      setStatus(r.data);
      toast.success(enabled ? `${META[kind].title}: importación automática activada` : `${META[kind].title}: importación automática pausada`);
    } catch { toast.error('No se pudo cambiar'); }
    finally { setBusy(null); }
  };

  if (!status) return <div className="bg-white rounded-2xl p-6 shadow-sm flex justify-center"><Loader2 className="animate-spin text-[#38B2AC]" /></div>;

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="automation-panel">
      <h2 className="font-semibold text-lg">Importaciones automáticas</h2>
      <p className="text-sm text-gray-500 mb-4">Solo afectan a las tareas programadas. Los botones «Importar ahora» del Admin y las importaciones por la API de Grokbot siguen funcionando aunque estén apagadas.</p>
      <div className="divide-y divide-gray-100">
        {Object.entries(META).map(([kind, m]) => {
          const s = status[kind];
          return (
            <div key={kind} className="py-4 flex items-start gap-4" data-testid={`automation-row-${kind}`}>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${s.enabled ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-400'}`}><m.icon size={20} /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{m.title}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${s.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'}`} data-testid={`automation-status-${kind}`}>{s.enabled ? 'Activa' : 'Pausada'}</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">{m.help}</p>
                <p className="text-xs text-gray-400 mt-1">Última ejecución: {fmt(s.last_run_at)}{s.changed_at ? ` · Cambiado ${fmt(s.changed_at)} por ${s.changed_by || 'admin'}` : ''}</p>
              </div>
              <Switch checked={s.enabled} disabled={busy === kind} onCheckedChange={(v) => toggle(kind, v)} data-testid={`automation-switch-${kind}`} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const AutomationNotice = ({ kind }) => {
  const [item, setItem] = useState(null);
  useEffect(() => { api.getAutomation().then(r => setItem(r.data[kind])).catch(() => {}); }, [kind]);
  if (!item || item.enabled) return null;
  return (
    <div className="flex items-center gap-2 bg-amber-50 border border-amber-100 text-amber-800 text-xs px-3 py-2 rounded-xl mb-4" data-testid={`automation-notice-${kind}`}>
      <PauseCircle size={14} />
      Importación automática pausada{item.changed_at ? ` desde el ${fmtDay(item.changed_at)}` : ''} · la alimenta Grokbot por la API
    </div>
  );
};

export default AutomationPanel;
